import { formatDateTime } from "../features/time/Time";
import Text from "@amzn/meridian/text";
import React from "react";
import Tooltip from "@amzn/meridian/tooltip";
import Link from "@amzn/meridian/link";
import Icon from "@amzn/meridian/icon";
import exportSmallTokens from "@amzn/meridian-tokens/base/icon/export-small";
import Button from "@amzn/meridian/button";
import copyTokens from "@amzn/meridian-tokens/base/icon/copy";
import { kebabCase, lowerCase, toLower, upperFirst } from "lodash";

export const DEFAULT_DATE_FORMAT = "YYYY-MM-DD";

export const formatString = (text: string | number) => {
    if (!text) return "-";

    return text;
};

export const formatNumber = (text: string | number) => {
    if (text === null || text === undefined || text === "") return "";

    const numStr = String(text);
    const [intPart, fracPart] = numStr.split(".");

    if (isNaN(Number(intPart))) return "-";

    const formattedIntPart = new Intl.NumberFormat("en-US", {
        style: "decimal",
        useGrouping: true,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Number(intPart));

    if (fracPart === undefined) {
        return formattedIntPart;
    } else {
        return `${formattedIntPart}.${fracPart.slice(0, 4)}`;
    }
};

export const formatNumberPdf = value => {
    if (!value) return "0";

    return Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

export const formatCurrencyUSD = value => {
    if (!value) return "$0.00";

    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

export const formatStringWithUnit = (text: string, unit: string) => {
    let formattedText = text || "";
    let formattedUnit = unit || "";

    formattedText = formatNumber(formattedText);

    formattedUnit =
        formattedUnit === "SQUARE FEET"
            ? "sqft"
            : formattedUnit === "ACRES"
              ? "acres"
              : formattedUnit;

    if (formattedText) {
        return `${formattedText} ${formattedUnit}`;
    }

    return "-";
};

export const formatDate = (date: string | number) => {
    if (!date) return "-";

    return formatDateTime(date);
};

export const formatLongString = (text: string) => {
    if (!text) return "-";

    if (text.length < 16) {
        return text;
    }

    return (
        <Tooltip
            position="top"
            title={
                <>
                    {text}
                    <Button type="icon" onClick={() => navigator.clipboard.writeText(text)}>
                        <Icon color={"inverted"} tokens={copyTokens}></Icon>
                    </Button>
                </>
            }
        >
            <span
                style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >{`${text.slice(0, 4)} ... ${text.slice(-4)}`}</span>
        </Tooltip>
    );
};

export const formatPhoneToolLink = (alias: string) => {
    if (!alias) return "-";

    return (
        <Text color="primary" truncate={true}>
            <Link
                target="_blank"
                rel="noopener noreferrer"
                href={"https://phonetool.amazon.com/users/" + alias}
            >
                {alias} <Icon tokens={exportSmallTokens} />
            </Link>
        </Text>
    );
};

export const formatToStartCase = (text: string) => {
    return upperFirst(lowerCase(text));
};

export const getTableKey = (documentType, propertyType) => {
    return kebabCase(`${documentType}-${toLower(propertyType.split("_")[0])}`);
};

export const removeRealOrPersonal = text => {
    return text && text.replace(new RegExp("\\((Real|Personal)\\)"), "");
};

export const formatInputWithPrefixAndSuffix = (
    text: string | number,
    prefix: string = "",
    suffix: string = ""
) => {
    if (text === 0) return `${prefix}${text}${suffix}`;
    if (text === "") return "-";
    if (isNumber(text)) return `${prefix}${formatNumber(text)}${suffix}`;

    return `${prefix}${text}${suffix}`;
};

export const isNumber = value => {
    return !isNaN(value) && !isNaN(parseFloat(value));
};

export const formatPercentage = (value: number): string =>
    value ? `${value.toFixed(2)}%` : "0.00%";

export const formatPercentageBlank = (value: number): string =>
    value ? `${value.toFixed(2)}%` : "-";

export const formatCurrencyBlankUSD = value => {
    if (!value) return "-";

    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};
